import RhfMaskedPhoneField from "@/components/Forms/RhfMaskedPhoneField/RhfMaskedPhoneField.tsx";
import { stateAbbreviations } from "@/constants/stateAbbreviations.ts";
import { Button, Grid2 as Grid, Link, MenuItem, Paper, Typography } from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { useState } from "react";
import { useFormContext } from "react-hook-form";

type Props = {
    onSaveAdjuster?: () => void;
    onCancelAddAdjuster?: () => void;
    isAddingAdjuster?: boolean;
};

const AdjusterInformation = ({
    onSaveAdjuster,
    onCancelAddAdjuster,
    isAddingAdjuster = false,
}: Props) => {
    const form = useFormContext();
    const [isEditing, setIsEditing] = useState(false);
    const [originalValues, setOriginalValues] = useState({
        namePrefix: "",
        nameFirst: "",
        nameI: "",
        nameLast: "",
        nameSuffix: "",
        nameAKA: "",
        phoneNumber: "",
        phoneExtension: "",
        faxNumber: "",
        email: "",
        address: "",
        address2: "",
        city: "",
        state: "",
        zip: "",
    });

    const adjusterId = form.watch("idAdjuster");

    const handleEditClick = () => {
        if (isEditing) {
            form.reset({
                ...form.getValues(),
                namePrefix: originalValues.namePrefix,
                nameFirst: originalValues.nameFirst,
                nameI: originalValues.nameI,
                nameLast: originalValues.nameLast,
                nameSuffix: originalValues.nameSuffix,
                nameAKA: originalValues.nameAKA,
                phoneNumber: originalValues.phoneNumber,
                phoneExtension: originalValues.phoneExtension,
                faxNumber: originalValues.faxNumber,
                email: originalValues.email,
                address: originalValues.address,
                address2: originalValues.address2,
                city: originalValues.city,
                state: originalValues.state,
                zip: originalValues.zip,
            });
        } else {
            const currentValues = form.getValues();
            setOriginalValues({
                namePrefix: currentValues.namePrefix,
                nameFirst: currentValues.nameFirst,
                nameI: currentValues.nameI,
                nameLast: currentValues.nameLast,
                nameSuffix: currentValues.nameSuffix,
                nameAKA: currentValues.nameAKA,
                phoneNumber: currentValues.phoneNumber,
                phoneExtension: currentValues.phoneExtension,
                faxNumber: currentValues.faxNumber,
                email: currentValues.email,
                address: currentValues.address,
                address2: currentValues.address2,
                city: currentValues.city,
                state: currentValues.state,
                zip: currentValues.zip,
            });
        }
        setIsEditing(!isEditing);
    };

    const handleSaveAdjuster = () => {
        if (onSaveAdjuster) {
            onSaveAdjuster();
        }
    };

    const handleCancelAddAdjuster = () => {
        if (onCancelAddAdjuster) {
            onCancelAddAdjuster();
        }
    };

    if (!(adjusterId || isAddingAdjuster)) {
        return null;
    }

    const fieldsEditable = isEditing || isAddingAdjuster;

    return (
        <Grid container>
            <Grid size={12}>
                <Paper
                    sx={{
                        pt: 1,
                        backgroundColor: "#eee",
                    }}
                >
                    <Grid container sx={{ px: 2, pb: 1 }}>
                        <Grid size={6}>
                            <Typography variant="h6" sx={{ color: "#666" }}>
                                {isAddingAdjuster
                                    ? "New Adjuster Information"
                                    : "Adjuster Information"}
                            </Typography>
                        </Grid>
                        <Grid size={6} sx={{ textAlign: "right" }}>
                            {!isAddingAdjuster && (
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={handleEditClick}
                                    sx={{
                                        color: "#4CAF50",
                                        textDecoration: "underline",
                                        cursor: "pointer",
                                        fontSize: "14px",
                                    }}
                                >
                                    {isEditing ? "Cancel Edit" : "Edit Adjuster Information"}
                                </Link>
                            )}
                            {isAddingAdjuster && (
                                <Grid container spacing={1} sx={{ justifyContent: "flex-end" }}>
                                    <Grid>
                                        <Button
                                            size="small"
                                            onClick={handleCancelAddAdjuster}
                                            sx={{
                                                fontSize: "12px",
                                                minWidth: "auto",
                                                px: 1,
                                            }}
                                        >
                                            Cancel
                                        </Button>
                                    </Grid>
                                    <Grid>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            onClick={handleSaveAdjuster}
                                            sx={{
                                                fontSize: "12px",
                                                minWidth: "auto",
                                                px: 1,
                                            }}
                                        >
                                            Save Adjuster
                                        </Button>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>

                    <Grid
                        container
                        sx={{
                            borderRadius: "8px",
                            p: 1,
                        }}
                    >
                        <Grid size={6} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"nameFirst"}
                                label={"First Name *"}
                                required={isAddingAdjuster}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                                rules={
                                    isAddingAdjuster
                                        ? {
                                              required: "First Name is required",
                                          }
                                        : {}
                                }
                            />
                        </Grid>
                        <Grid size={6} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"nameLast"}
                                label={"Last Name *"}
                                required={isAddingAdjuster}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                                rules={
                                    isAddingAdjuster
                                        ? {
                                              required: "Last Name is required",
                                          }
                                        : {}
                                }
                            />
                        </Grid>
                        <Grid size={4} sx={{ p: 1 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name={"namePrefix"}
                                label={"Prefix"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="Mr.">Mr.</MenuItem>
                                <MenuItem value="Mrs.">Mrs.</MenuItem>
                                <MenuItem value="Ms.">Ms.</MenuItem>
                                <MenuItem value="Dr.">Dr.</MenuItem>
                            </RhfTextField>
                        </Grid>
                        <Grid size={4} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"nameI"}
                                label={"MI"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                                inputProps={{ maxLength: 1 }}
                            />
                        </Grid>
                        <Grid size={4} sx={{ p: 1 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name={"nameSuffix"}
                                label={"Suffix"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            >
                                <MenuItem value="">None</MenuItem>
                                <MenuItem value="Jr.">Jr.</MenuItem>
                                <MenuItem value="Sr.">Sr.</MenuItem>
                                <MenuItem value="II">II</MenuItem>
                                <MenuItem value="III">III</MenuItem>
                                <MenuItem value="IV">IV</MenuItem>
                            </RhfTextField>
                        </Grid>
                        <Grid size={12} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"nameAKA"}
                                label={"AKA/Preferred"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={6} sx={{ p: 1 }}>
                            <RhfMaskedPhoneField
                                name={"phoneNumber"}
                                label={"Phone Number"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={2} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"phoneExtension"}
                                label={"Ext"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                                inputProps={{ maxLength: 10 }}
                            />
                        </Grid>
                        <Grid size={6} sx={{ p: 1 }}>
                            <RhfMaskedPhoneField
                                name={"faxNumber"}
                                label={"Fax Number"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={12} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"email"}
                                label={"Email"}
                                type="email"
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                                rules={
                                    isAddingAdjuster
                                        ? {
                                              pattern: {
                                                  value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                  message: "Please enter a valid email address",
                                              },
                                          }
                                        : {}
                                }
                            />
                        </Grid>
                        <Grid size={9} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"address"}
                                label={"Address"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={3} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"address2"}
                                label={"Apt/Suite"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={6} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"city"}
                                label={"City"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                        <Grid size={2} sx={{ p: 1 }}>
                            <RhfTextField
                                select
                                control={form.control}
                                name={"state"}
                                label={"State"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                }}
                            >
                                {stateAbbreviations.map((type) => (
                                    <MenuItem key={`state-${type}`} value={type}>
                                        {type}
                                    </MenuItem>
                                ))}
                            </RhfTextField>
                        </Grid>
                        <Grid size={4} sx={{ p: 1 }}>
                            <RhfTextField
                                control={form.control}
                                name={"zip"}
                                label={"Zip"}
                                disabled={!fieldsEditable}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                }}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default AdjusterInformation;
